import qs from 'qs';

import useMutation from '../../../ReactQuery/useMutation';
import useQuery from '../../../ReactQuery/useQuery';
import { get, patch, put } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { Scope } from '../../../../shared/issuesV2';
import type { PackageLabel } from '../../../../shared/model-typings/packagelabel';
import type { PackageLabelAssignmentWithLabel } from '../../../../shared/model-typings/packagelabelassignment';
import type { PackageLabelScope } from '../../../../shared/packageLabels';
import type { VersionedLabelOption } from '../../../components/modals/ModalManagePackageLabels/types';
import { IssueScopeKind } from '../../../../shared/issuesV2/common';

export type PackageLabelResponse = {
  packageLabels: PackageLabel[];
};

function getOrganizationPackageLabels() {
  return get<PackageLabelResponse>('/api/package-labels', true);
}

/**
 * Fetch all the package labels that an organization has defined.
 */
export function useOrganizationPackageLabels() {
  return useQuery<PackageLabelResponse>({
    queryFn: () => getOrganizationPackageLabels(),
    queryKey: ['packageLabels'],
  });
}

export type PackageLabelAssignmentResponse = {
  packageLabelAssignments: PackageLabelAssignmentWithLabel[];
};

function getPackageLabelAssignments(
  packageId: string,
  packageVersion?: string,
  scopeId?: string,
  scopeType?: PackageLabelScope,
  shouldIncludePackageWideLabels?: boolean,
  shouldIncludeRevisionScopedLabels?: boolean,
  shouldIncludeOrgScopedLabels?: boolean,
) {
  const filterParams = {
    filters: {
      packageId: nuc(packageId),
      ...(packageVersion ? { packageVersion: nuc(packageVersion) } : {}),
      ...(scopeId ? { scopeId: nuc(scopeId) } : {}),
      ...(scopeType ? { scope: scopeType } : {}),
      ...(shouldIncludePackageWideLabels ? { shouldIncludePackageWideLabels } : {}),
      ...(shouldIncludeRevisionScopedLabels ? { shouldIncludeRevisionScopedLabels } : {}),
      ...(shouldIncludeOrgScopedLabels ? { shouldIncludeOrgScopedLabels } : {}),
    },
  };
  const params = qs.parse(qs.stringify(filterParams));
  return get<PackageLabelAssignmentResponse>(`/api/package-label-assignments`, true, params);
}

/**
 * Fetch all the package labels assigned to a dependency.
 *
 * Does nothing until a package id is known. Callers mount before a dependency is
 * selected (the Assign Labels modal on the dependencies page, the issues details
 * panel) and pass '' in the meantime; the server treats an empty packageId as
 * "no filter" and returns every assignment in the organization, which on large
 * orgs is hundreds of thousands of rows on every page load.
 */
export function usePackageLabelAssignments(
  packageId: string,
  packageVersion?: string,
  scope?: Scope,
  shouldIncludePackageWideLabels?: boolean,
  shouldIncludeRevisionScopedLabels?: boolean,
  shouldIncludeOrgScopedLabels?: boolean,
) {
  const scopeType = scope && 'type' in scope ? scope.type : undefined;
  const scopeId = scope && 'id' in scope && scopeType === IssueScopeKind.Project ? scope.id : undefined;
  // include scope only for 'project'
  const PackageLabelScope: PackageLabelScope | undefined = scopeType === IssueScopeKind.Project ? 'project' : undefined;

  return useQuery<PackageLabelAssignmentResponse>({
    enabled: packageId !== '',
    queryKey: ['packageLabels', packageId, packageVersion, scopeId, PackageLabelScope, shouldIncludePackageWideLabels, shouldIncludeRevisionScopedLabels, shouldIncludeOrgScopedLabels],
    queryFn: () => getPackageLabelAssignments(
      packageId,
      packageVersion,
      scopeId,
      PackageLabelScope,
      shouldIncludePackageWideLabels,
      shouldIncludeRevisionScopedLabels,
      shouldIncludeOrgScopedLabels,
    ),
  });
}

/**
 * PUT to the bulk update endpoint to handle the assignment and un-assignment of labels to a package in a single scope.
 */
function putPackageLabelAssignments(
  packageId: string,
  scope: PackageLabelScope,
  newLabelIds: VersionedLabelOption,
  scopeId?: string,
) {
  // convert the sets to arrays for network transport
  const newLabelIdsArrays = Object.fromEntries(
    Object.entries(newLabelIds)
      .map(([versionKey, labelIds]) => [versionKey, Array.from(labelIds)]),
  );
  return put(`/api/package-label-assignments`, {
    packageId,
    scope,
    newLabelIds: newLabelIdsArrays,
    scopeId,
  });
}

export type UpdatePackageLabelAssignmentsRequest = {
  newLabelIds: VersionedLabelOption;
  scope: PackageLabelScope;
  scopeId?: string;
};

/**
 * Update the labels assigned to a package.
 */
export function useUpdatePackageLabelAssignments(
  packageId: string,
) {
  const updatePackageLabelAssignments = useMutation({
    mutationFn: ({
      newLabelIds,
      scope,
      scopeId,
    }: UpdatePackageLabelAssignmentsRequest) =>
      putPackageLabelAssignments(packageId, scope, newLabelIds, scopeId),
  });

  return { updatePackageLabelAssignments };
}

export type PatchPackageLabelRequest = {
  labelId: number;
  newLabel: string;
};

function patchPackageLabel(labelId: number, newLabel: string) {
  return patch(`/api/package-labels/${labelId}`, { name: newLabel });
}

/**
 * PATCH a package label to change its name in place across all usages.
 */
export function usePatchPackageLabel() {
  return useMutation({
    mutationFn: ({
      labelId,
      newLabel,
    }: PatchPackageLabelRequest) => patchPackageLabel(labelId, newLabel),
  });
}
